import { useCallback, useEffect, useState } from 'react';
import { getDailySpeaking } from '../api/dailySpeaking';
import { getTodayInChina } from '../../../utils/timestampUtils';

/** @typedef {import('../api/dailySpeaking').DailySpeakingResponse} DailySpeakingResponse */

// Module-level so the dashboard card does not refire on every tab switch (it
// remounts each time). Cleared when the page answers, because answering changes
// whether today is complete. Keyed by the China date, so a tab left open past
// midnight fetches the new day instead of showing yesterday's.
const inFlight = new Map();

const cacheKey = (studentId) => `${studentId}::${getTodayInChina()}`;

/** Only today exists, so no date is ever sent. */
function load(studentId) {
  const key = cacheKey(studentId);
  if (!inFlight.has(key)) {
    const request = getDailySpeaking(studentId);
    inFlight.set(key, request.catch((err) => {
      inFlight.delete(key);
      throw err;
    }));
  }
  return inFlight.get(key);
}

/** Drop this student's cached days — call after an answer is graded. */
export function invalidateDailySpeaking(studentId) {
  for (const key of [...inFlight.keys()]) {
    if (key.startsWith(`${studentId}::`)) inFlight.delete(key);
  }
}

/**
 * Fetch a student's 每日口语 day. A failed fetch is surfaced as `error`, never
 * thrown: the dashboard card stays invisible, the page offers a retry.
 *
 * @param {string|null|undefined} studentId - falsy defers the fetch entirely
 * @returns {{ loading: boolean, error: Error|null,
 *             data: DailySpeakingResponse|{enabled: false}|null, refetch: () => void }}
 */
export function useDailySpeaking(studentId) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!studentId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    load(studentId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [studentId, attempt]);

  const refetch = useCallback(() => {
    invalidateDailySpeaking(studentId);
    setAttempt((n) => n + 1);
  }, [studentId]);

  return { loading, error, data, refetch };
}
